import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { isTooShortToSearch } from "../../../../../utils/functions/listSearchThrottle";

// Free-text variant of the filter-bar Filter (see ListHeader.tsx): a text
// field with the size and look of the dropdown filters (input-cus + but-48).
// The typed term is the filter's uncommitted value exactly like a dropdown
// pick: every change goes up through onChange, so "Apply Filters" carries
// it (one request, together with the dropdown choices), "Reset filters" and
// closing the bar drop it, and the Apply button counts it. Unlike the
// dropdowns the field also commits by itself - after Enter or debounceMs
// without a keystroke - so the list sends one request per typing pause,
// never one per keystroke, and never a second one for a value Apply already
// sent (ListHeader skips a commit that is already active). A term shorter
// than minChars (whitespace trimmed) is neither handed up nor committed; the
// empty term is both (it removes the filter), as is the x. An outside change
// of the uncommitted value (Reset filters, a remembered set, the bar
// closing) replaces what is typed and cancels a pending commit. The term is
// one value: a comma inside it ("white, 877") is part of the text and goes
// up as typed - splitting it is the API's job (its meaning: all of them).
// An optional hint is the field's tooltip (title) that says so.
export const DEFAULT_TEXT_FILTER_DEBOUNCE_MS = 600;
export const DEFAULT_TEXT_FILTER_MIN_CHARS = 2;

export interface TextFilterCommitEvent {
  key: string;
  // undefined = the filter is removed (field cleared).
  value: string | undefined;
}

interface TextFilterProps {
  filterKey: string;
  initialValue?: string | number | boolean;
  defaultTitle: string;
  debounceMs?: number;
  minChars?: number;
  // Tooltip on the field (title); none = no title attribute.
  hint?: string;
  // The term as the filter's uncommitted value (see above).
  onChange: (event: TextFilterCommitEvent) => void;
  // The term goes to the list's request.
  onCommit: (event: TextFilterCommitEvent) => void;
}

export function TextFilter({
  filterKey,
  initialValue,
  defaultTitle,
  debounceMs = DEFAULT_TEXT_FILTER_DEBOUNCE_MS,
  minChars = DEFAULT_TEXT_FILTER_MIN_CHARS,
  hint,
  onChange,
  onCommit,
}: TextFilterProps) {
  const { t } = useTranslation();
  const external = initialValue === undefined ? "" : "" + initialValue;
  const [value, setValue] = useState<string>(external);
  // Last term handed up through onChange (trimmed; "" = none). The
  // uncommitted value coming back down as initialValue is told apart from
  // an outside change by it.
  const lastSentRef = useRef<string>(external.trim());
  // Last term handed to onCommit (trimmed; "" = none): an unchanged term is
  // never committed again (a trailing space costs no request).
  const lastCommittedRef = useRef<string>(external.trim());
  // Read through refs so the memoised debounce below never has to be
  // rebuilt for a new handler instance.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const debouncedCommit = useMemo(
    () =>
      debounce((raw: string) => {
        const term = raw.trim();
        // Too short: nothing is sent and the field keeps its hint until the
        // user types more, clears the field or presses Enter on a term that
        // is long enough. The empty term (clearing) always goes through.
        if (isTooShortToSearch(term, minChars)) return;
        if (term === lastCommittedRef.current) return;
        lastCommittedRef.current = term;
        onCommitRef.current({
          key: filterKey,
          value: term === "" ? undefined : term,
        });
      }, debounceMs),
    [filterKey, debounceMs, minChars],
  );

  useEffect(() => {
    if (external.trim() !== lastSentRef.current) {
      lastSentRef.current = external.trim();
      lastCommittedRef.current = external.trim();
      setValue(external);
      debouncedCommit.cancel();
    }
  }, [external, debouncedCommit]);

  useEffect(() => {
    return () => {
      debouncedCommit.cancel();
    };
  }, [debouncedCommit]);

  function send(raw: string) {
    const term = raw.trim();
    if (isTooShortToSearch(term, minChars)) return;
    if (term === lastSentRef.current) return;
    lastSentRef.current = term;
    onChangeRef.current({
      key: filterKey,
      value: term === "" ? undefined : term,
    });
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
    send(event.target.value);
    debouncedCommit(event.target.value);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      // Commit at once instead of waiting out the debounce delay.
      debouncedCommit.flush();
    }
  }

  function handleRemove() {
    setValue("");
    send("");
    debouncedCommit("");
    debouncedCommit.flush();
  }

  const tooShort = isTooShortToSearch(value, minChars);

  return (
    <div className="relative but-48 !text-xs">
      <div className="input-cus but-48 flex items-center px-2">
        <input
          type="text"
          aria-label={defaultTitle}
          placeholder={defaultTitle}
          title={hint}
          className="w-full h-full border-none bg-transparent text-gray-600 text-xs font-medium outline-none"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {tooShort && (
          <span
            className="flex items-center pl-1 text-gray-400 text-xxs whitespace-nowrap"
            data-testid="text-filter-min-chars-hint"
          >
            {t("compGenericTable.searchMinChars", { n: minChars })}
          </span>
        )}
      </div>
      {value !== "" && (
        <button
          type="button"
          className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors z-10"
          onClick={handleRemove}
          title={t("compGenericTable.removeFilterTitle", {
            title: defaultTitle,
          })}
        >
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>
      )}
    </div>
  );
}
